/* ECharts wiring for the Overview page.
   Visual reference: docs/design/overview.html. Normative rules: docs/design/spec/.

   ECharts is registered here rather than in each component so the module list is
   in one place: only the four chart types and five components the Overview
   actually draws are pulled in, which is what keeps the tree-shaken build a
   fraction of the full `echarts` bundle. */

import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { HeatmapChart, LineChart, PieChart } from "echarts/charts";
import { AriaComponent, CalendarComponent, GridComponent, LegendComponent, TooltipComponent, VisualMapComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  LineChart,
  PieChart,
  HeatmapChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CalendarComponent,
  VisualMapComponent,
  AriaComponent,
  CanvasRenderer,
]);

export { echarts };

/* Canvas cannot read CSS custom properties, so every colour a chart draws has to
   be resolved to a literal first. Reading them off :root at runtime keeps
   tokens.css the single source of truth — no hex is duplicated into JS. */
const TOKENS = [
  "--text-primary",
  "--text-secondary",
  "--surface-page",
  "--surface-sunken",
  "--border-subtle",
  "--action-primary",
  "--series-visits",
  "--series-reports",
  "--series-answers",
  "--chart-grid",
  "--chart-axis",
  "--success-500",
  "--warning-500",
  "--danger-500",
  "--danger-700",
  "--success-700",
  "--brand-50",
  "--brand-200",
  "--brand-400",
  "--brand-500",
  "--brand-600",
  "--brand-800",
];

export function useTokens() {
  return useMemo(() => {
    const style = getComputedStyle(document.documentElement);
    const out = {};
    TOKENS.forEach(name => {
      out[name.slice(2)] = style.getPropertyValue(name).trim();
    });
    return out;
  }, []);
}

/* Series colours a chart draws, keyed the way the API keys its data. */
export const seriesColor = (t, key) =>
  ({
    visits: t["series-visits"],
    reports: t["series-reports"],
    answers: t["series-answers"],
    survey_answers: t["success-500"],
    approved: t["success-500"],
    on_verification: t["warning-500"],
    not_approved: t["danger-500"],
    submitted: t["series-visits"],
    reviewed: t["series-reports"],
    photo: t["brand-500"],
    dropdown: t["series-reports"],
    numeric: t["success-500"],
    text: t["series-answers"],
    boolean: t["warning-500"],
    date: t["brand-200"],
  }[key]);

export const baseText = t => ({
  fontFamily: getComputedStyle(document.body).fontFamily,
  fontSize: 12,
  color: t["chart-axis"],
});

export const baseTooltip = t => ({
  backgroundColor: t["surface-page"],
  borderColor: t["border-subtle"],
  borderWidth: 1,
  padding: [8, 12],
  textStyle: { ...baseText(t), color: t["text-primary"] },
  extraCssText: "box-shadow:var(--elevation-3);border-radius:var(--radius-md);",
});

/**
 * Own one ECharts instance for the lifetime of a container.
 *
 * The instance is created once and re-configured with `setOption(option, true)`
 * whenever `option` changes — disposing and re-initialising on every render
 * leaks canvases and loses the resize observer. A ResizeObserver is used rather
 * than a window listener because the cards resize when the sidebar collapses,
 * which fires no window resize at all.
 */
export function useChart(option) {
  const host = useRef(null);
  const chart = useRef(null);

  useEffect(() => {
    if (!host.current) return undefined;
    chart.current = echarts.init(host.current, null, { renderer: "canvas" });
    const observer = new ResizeObserver(() => chart.current.resize());
    observer.observe(host.current);
    return () => {
      observer.disconnect();
      chart.current.dispose();
      chart.current = null;
    };
  }, []);

  useEffect(() => {
    if (chart.current && option) chart.current.setOption(option, true);
  }, [option]);

  return host;
}
